import React from "react";

import { Location, Outlet, useLocation, useNavigate } from "react-router-dom";

import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";

import MenuItem from "commons/components/layout/menu/MenuItem";

import { AccountLogoutContextProvider } from "commons/context/accountLogout";
import { ApiErrorContextProvider, useApiErrorContext } from "commons/context/apiError";
import { LayoutContextProvider } from "commons/context/layout";
import { useMenuOpenActionsContext, useMenuOpenContext } from "commons/context/menu";

import useClassNameExtenderPull from "commons/hooks/useClassNameExtenderPull";
import { StateApiErrorType } from "commons/hooks/useNavigateToApiErrorPage";
import useUiExtenderPull from "commons/hooks/useUiExtenderPull";

import { MenuGroupType, MenusType, MenuType } from "commons/types/menu";
import { PathWayType, RedirectsType, RoutesType } from "commons/types/route";
import { SitemapItemType, SitemapType } from "commons/types/sitemap";

import { makeUrl } from "commons/utils/http";
import storage from "commons/utils/storage";
import { checkUserAuthorities } from "commons/utils/utils";

export function calculateRoutes(sitemap: SitemapType) {
    const routes: RoutesType = [];

    const walkThroughMenus = (
        children: Array<SitemapItemType>,
        previousPath: string,
        previousPathWay: Array<PathWayType>,
        previousPathParams: { [x: string]: string }
    ) => {
        children.forEach((item: SitemapItemType) => {
            const url = previousPath + makeUrl(item.path, item.pathParams);
            const pathParams = { ...previousPathParams, ...item.pathParams };

            if (item.element) {
                routes.push({
                    path: url,
                    neededAuthorities: item.neededAuthorities,
                    oneOf: item.oneOf,
                    element: item.element,
                    headerImage: item.headerImage,
                    children: item.children,
                    extendedWidth: item.extendedWidth,
                    maxWidthUnset: item.maxWidthUnset,
                    fullWidth: item.fullWidth,
                    dontShowBackButton: item.dontShowBackButton,
                    dontShowPathWay: item.dontShowPathWay,
                    dontShowInMenu: item.dontShowInMenu,
                    pathwayElements: [
                        ...previousPathWay,
                        { label: item.label, pathwayLabel: item.pathwayLabel, lang: item.labelLang },
                    ],
                    label: item.label,
                    pathParams: pathParams,
                });
            }

            if (Array.isArray(item.children)) {
                walkThroughMenus(
                    item.children,
                    url,
                    [...previousPathWay, { path: url, label: item.label,  pathwayLabel: item.pathwayLabel, lang: item.labelLang }],
                    pathParams
                );
            }
        });
    };

    sitemap.forEach((mainGroup) => {
        mainGroup.groups.forEach((group) => {
            walkThroughMenus(
                group.items,
                group.path ? makeUrl(group.path, group.pathParams) : "",
                group.label ? [{ label: group.label }] : [],
                group.pathParams ? group.pathParams : {}
            );
        });
    });

    return routes;
}

function calculateSubMenusRedirects(
    previousPath: string,
    items: Array<SitemapItemType>,
    redirects: RedirectsType
) {
    items.forEach((item) => {
        const newPreviosPath = previousPath + makeUrl(item.path, item.pathParams);

        if (item.redirectTo) {
            redirects.push({
                path: newPreviosPath,
                redirectTo: newPreviosPath + item.redirectTo,
            });
        }

        if (item.children) {
            calculateSubMenusRedirects(newPreviosPath, item.children, redirects);
        }
    });
}

export function calculateRedirects(sitemap: SitemapType) {
    const redirects: RedirectsType = [];

    sitemap.forEach((mainGroup) =>
        mainGroup.groups.forEach((group) =>
            calculateSubMenusRedirects(
                group.path ? makeUrl(group.path, group.pathParams) : "",
                group.items,
                redirects
            )
        )
    );

    return redirects;
}

function createSubMenus(
    userAuthorities: Array<string>,
    previousPath: string,
    item: SitemapItemType
): Array<MenuType> {
    const children: Array<MenuType> = [];

    if (item.withVisibleChildren && item.children) {
        item.children.forEach((child) => {
            if (
                child.dontShowInMenu !== true &&
                checkUserAuthorities(userAuthorities, child.neededAuthorities, child.oneOf)
            ) {
                const url = previousPath + makeUrl(child.path, child.pathParams);

                children.push({
                    path: url,
                    icon: child.icon,
                    label: child.label,
                    labelElement: child.labelElement,
                    notificationBadge: child.notificationBadge,
                    lang: item.labelLang,
                    neededAuthorities: item.neededAuthorities,
                    oneOf: item.oneOf,
                    children: createSubMenus(userAuthorities, url, child),
                });
            }
        });
    }

    return children;
}

function createChildren(
    baseUrl: string,
    userAuthorities: Array<string>,
    item: SitemapItemType
): Array<MenuType> {
    const children: Array<MenuType> = [];

    if (item.children) {
        item.children.forEach((subMenu) => {
            if (
                subMenu.dontShowInMenu !== true &&
                checkUserAuthorities(userAuthorities, subMenu.neededAuthorities, subMenu.oneOf)
            ) {
                const url =
                    baseUrl +
                    makeUrl(item.path, item.pathParams) +
                    makeUrl(subMenu.path, subMenu.pathParams);

                children.push({
                    path: url,
                    icon: subMenu.icon,
                    label: subMenu.label,
                    labelElement: subMenu.labelElement,
                    notificationBadge: subMenu.notificationBadge,
                    lang: item.labelLang,
                    neededAuthorities: item.neededAuthorities,
                    oneOf: item.oneOf,
                    children: createSubMenus(userAuthorities, url, subMenu),
                });
            }
        });
    }

    return children;
}

export function calculateMenus(sitemap: SitemapType, userAuthorities: Array<string>) {
    const menus: Array<MenusType> = [];

    sitemap.forEach((mainGroup, mainIndex) => {
        const groups: Array<MenuGroupType> = [];

        const urls: Array<string> = [];

        mainGroup.groups.forEach((group, groupIndex) => {
            const tmpItems: Array<MenuType> = [];

            const baseUrl = group.path ? makeUrl(group.path, group.pathParams) : "";

            group.items.forEach((item) => {
                if (
                    item.dontShowInMenu !== true &&
                    checkUserAuthorities(userAuthorities, item.neededAuthorities, item.oneOf)
                ) {
                    const children: Array<MenuType> = createChildren(
                        baseUrl,
                        userAuthorities,
                        item
                    );

                    const tmpItem: MenuType = {
                        label: item.label,
                        icon: item.icon,
                        path: baseUrl + makeUrl(item.path, item.pathParams),
                        labelElement: item.labelElement,
                        notificationBadge: item.notificationBadge,
                        lang: item.labelLang,
                        neededAuthorities: item.neededAuthorities,
                        oneOf: item.oneOf,
                        children: children,
                    };

                    tmpItems.push(tmpItem);

                    urls.push(baseUrl + makeUrl(item.path, item.pathParams));
                }
            });

            if (tmpItems.length > 0) {
                groups.push({ items: tmpItems, index: groupIndex });
            }
        });

        if (groups.length > 0) {
            menus.push({
                elementTitle: mainGroup.elementTitle,
                accordionId: mainGroup.accordionId,
                groups: groups,
                urls: urls,
                index: mainIndex,
            });
        }
    });

    return menus;
}

export function calculateSinglePagesNotInMenu(
    sitemap: SitemapType,
    userAuthorities: Array<string>
) {
    const items: Array<MenuType> = [];

    sitemap.forEach((mainGroup) => {
        mainGroup.groups.forEach((group) => {
            group.items.forEach((item) => {
                if (
                    item.dontShowInMenu === true &&
                    checkUserAuthorities(userAuthorities, item.neededAuthorities, item.oneOf)
                ) {
                    const tmpItem: MenuType = {
                        label: item.label,
                        icon: item.icon,
                        path: makeUrl(item.path, item.pathParams),
                        labelElement: item.labelElement,
                        notificationBadge: item.notificationBadge,
                        lang: item.labelLang,
                        neededAuthorities: item.neededAuthorities,
                        oneOf: item.oneOf,
                        children: [],
                    };

                    items.push(tmpItem);
                }
            });
        });
    });

    return items;
}

function checkPathname(path: string, urls: Array<string>): boolean {
    let check: boolean = false;

    urls.forEach((url) => {
        if (path.startsWith(url)) {
            check = true;
        }
    });

    return check;
}

function LayoutBaseContent({
    sitemap,
    userAuthorities,
    footer,
    DisplayApiError,
    environment,
    environmentDescription,
    isImpersonation,
    logo,
    logoDescription,
    logoMobile,
    altLogo,
    bottomInfo,
    baseUiUrl,
    accountLogout,
    notifications,
    menuVersion,
}: PropsType) {
    const location = useLocation();

    const { isMenuOpen } = useMenuOpenContext();

    const { toogleMenuOpen, toggleMenuWhenClose } = useMenuOpenActionsContext();

    const apiError = useApiErrorContext();

    const menus: Array<MenusType> = React.useMemo(() => {
        return calculateMenus(sitemap, userAuthorities);
    }, [sitemap, userAuthorities]);

    const menuOpenClass: string = React.useMemo(
        () => (isMenuOpen ? " menu-open " : ""),
        [isMenuOpen]
    );

    const uiElements = useUiExtenderPull({ key: "right_main_content" });
    const extraClassNamesMainContent = useClassNameExtenderPull({ key: "main_content" });

    if (apiError.withError && apiError.state !== undefined) {
        return <DisplayApiError state={apiError.state} reloadPage />;
    }

    return (
        <>
            <a href={"#main"} className={"skip-to-main-content-link"}>
                {"Vai al contenuto"}
            </a>

            {environment && environment !== "PRD" && (
                <div className={"stg-bar"}>
                    <p>{environmentDescription}</p>
                </div>
            )}

            <header className={isImpersonation ? "impersonation" : ""}>
                <div className={`button-nav-spacer ${menuOpenClass}`} />

                <div className={"logo-title"}>
                    {logo && (
                        <img
                            className={`logo-desktop ${
                                logoMobile === undefined ? "always-visible" : ""
                            }`}
                            src={logo}
                            alt={altLogo}
                        />
                    )}
                    {logoMobile && <img className={"logo-mobile"} src={logoMobile} alt={altLogo} />}
                    {logoDescription && <div className={"desc-logo"}>{logoDescription}</div>}
                </div>

                <div className={"header-end-container"}>
                    {notifications}

                    {accountLogout}
                </div>
            </header>

            <div className={"structure"} data-testid={"structure"}>
                <button
                    type={"button"}
                    className={`header-icon ${menuOpenClass} ${
                        isImpersonation ? "impersonation" : ""
                    }`}
                    onClick={toogleMenuOpen}
                    data-testid={"toogleMenu"}
                    title={isMenuOpen ? "Chiudi menù" : "Apri menù"}
                >
                    <div className={"close-icon"}>
                        <CloseIcon />
                    </div>
                    <div className={"menu-icon"}>
                        <MenuIcon />
                    </div>
                </button>

                <nav className={`${menuOpenClass} ${menuVersion ?? ""}`}>
                    {menus.map((menu, index) => (
                        <MenuGroupItem
                            key={menu.index}
                            menus={menus}
                            menu={menu}
                            index={index}
                            location={location}
                            baseUiUrl={baseUiUrl}
                            userAuthorities={userAuthorities}
                            menuVersion={menuVersion}
                        />
                    ))}

                    {bottomInfo && (
                        <div className={"bottom-info-container"}>
                            <p>{bottomInfo}</p>
                        </div>
                    )}
                </nav>

                <main
                    className={`${menuOpenClass} ${extraClassNamesMainContent}`}
                    id={"main"}
                    onClick={toggleMenuWhenClose}
                >
                    <Outlet />
                </main>

                {uiElements}
            </div>

            {footer}
        </>
    );
}

function MenuGroupItem({
    menus,
    menu,
    index,
    location,
    baseUiUrl,
    userAuthorities,
    menuVersion,
}: {
    readonly menus: Array<MenusType>;
    readonly menu: MenusType;
    readonly index: number;
    readonly location: Location;
    readonly baseUiUrl?: string;
    readonly userAuthorities: Array<string>;
    readonly menuVersion?: string;
}) {
    const { accordionId } = menu;

    const navigate = useNavigate();

    const storageKey = React.useMemo(() => `menu-accordion-${accordionId}-state`, [accordionId]);

    const [open, setOpen] = React.useState<boolean>(() => {
        let storedOpen = storage.get(storageKey);

        if (storedOpen === null) {
            storedOpen = true;
        }

        return storedOpen;
    });

    const toggleOpen = React.useCallback(() => {
        setOpen((prevOpen) => !prevOpen);
    }, []);

    const onTitleClick = React.useCallback(() => {
        setOpen(true);

        for (const group of menu.groups) {
            for (const item of group.items) {
                if (checkUserAuthorities(userAuthorities, item.neededAuthorities, item.oneOf)) {
                    navigate(item.path);

                    return;
                }
            }
        }
    }, [navigate, menu, userAuthorities]);

    React.useEffect(() => {
        storage.set(storageKey, open);
    }, [storageKey, open]);

    return (
        <div className={`main-group-container ${index === menus.length - 1 ? "last-item" : ""}`}>
            {menu.elementTitle && (
                <div
                    className={`main-group-title ${
                        checkPathname(location.pathname, menu.urls) ? "active" : ""
                    } ${!open ? " not-open " : ""}`}
                >
                    <div
                        className={`element-title-container ${
                            accordionId !== undefined ? "hoverable" : ""
                        }`}
                    >
                        {accordionId === undefined && <div>{menu.elementTitle}</div>}

                        {accordionId && (
                            <>
                                <button className={"element-title"} onClick={() => onTitleClick()}>
                                    {menu.elementTitle}
                                </button>

                                <div className="accordion-button-container">
                                    <button className="accordion" onClick={() => toggleOpen()}>
                                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className={"main-group-groups"}>
                {open && (
                    <>
                        {menu.groups.map((group) => (
                            <div key={group.index} className={"group"}>
                                {group.items.map((menuItem) => (
                                    <MenuItem
                                        key={menuItem.path}
                                        baseUiUrl={baseUiUrl}
                                        menu={menuItem}
                                        menuVersion={menuVersion}
                                    />
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

type PropsType = {
    readonly sitemap: SitemapType;
    readonly userAuthorities: Array<string>;
    readonly accountLogout: JSX.Element;
    readonly isImpersonation?: boolean;
    readonly logo?: string;
    readonly logoMobile?: string;
    readonly altLogo?: string;
    readonly environment?: string;
    readonly environmentDescription?: string;
    readonly logoDescription?: string | JSX.Element;
    readonly footer?: JSX.Element;
    readonly bottomInfo?: string | JSX.Element;
    readonly notifications?: JSX.Element;
    readonly baseUiUrl?: string;
    readonly menuVersion?: string;
    readonly DisplayApiError: ({
        state,
        reloadPage,
    }: {
        state: StateApiErrorType;
        reloadPage: boolean;
    }) => JSX.Element;
};

function LayoutBase(props: PropsType) {
    const layoutContextValue = React.useMemo(
        () => ({ baseUiUrl: props.baseUiUrl }),
        [props.baseUiUrl]
    );

    return (
        <LayoutContextProvider layout={layoutContextValue}>
            <AccountLogoutContextProvider>
                <ApiErrorContextProvider>
                    <LayoutBaseContent {...props} />
                </ApiErrorContextProvider>
            </AccountLogoutContextProvider>
        </LayoutContextProvider>
    );
}

export default LayoutBase;
